import { API_URL } from '../constants/api-url';
import { getAuthHeader, AuthError } from '../utils/getAuthHeader';

// Base API configuration
const API_BASE_URL = `${API_URL}/api/v1`;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public isAuthError: boolean = false,
    public response?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    if (this.isAuthError) {
      return 'Authentication required. Please log in again.';
    }

    switch (this.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data.';
      case 422:
        return 'The data provided is invalid or incomplete.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error occurred. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return this.message || 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Get error title for notifications
   */
  getTitle(): string {
    if (this.isAuthError) {
      return 'Authentication Error';
    }

    switch (this.status) {
      case 400:
        return 'Invalid Request';
      case 403:
        return 'Permission Error';
      case 404:
        return 'Not Found';
      case 409:
        return 'Conflict Error';
      case 422:
        return 'Validation Error';
      case 429:
        return 'Rate Limit Error';
      case 500:
        return 'Server Error';
      case 502:
      case 503:
      case 504:
        return 'Service Error';
      default:
        return 'Error';
    }
  }
}

// Generic API client
export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  private async getRequestHeaders(
    additionalHeaders?: Record<string, string>
  ): Promise<Record<string, string>> {
    try {
      const authHeader = await getAuthHeader();

      return {
        ...this.defaultHeaders,
        ...authHeader,
        ...additionalHeaders,
      };
    } catch (error) {
      if (error instanceof AuthError) {
        // For critical auth errors, we still want to attempt the request
        // but let the backend handle the rejection with proper 401 response
        console.warn('Authentication error, proceeding without auth header:', error.message);

        return {
          ...this.defaultHeaders,
          ...additionalHeaders,
        };
      }
      throw error;
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = await this.getRequestHeaders(options.headers as Record<string, string>);

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const isAuthError = response.status === 401;
        let errorMessage = `HTTP error! status: ${response.status}`;
        let responseData = null;

        // Try to get error details from response body
        try {
          responseData = await response.json();
          if (responseData?.message) {
            errorMessage = responseData.message;
          } else if (responseData?.error) {
            errorMessage = responseData.error;
          }
        } catch {
          // Ignore JSON parsing errors, use default message
        }

        const apiError = new ApiError(response.status, errorMessage, isAuthError, responseData);

        throw apiError;
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null as T;
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      const hasJsonContent = contentType && contentType.includes('application/json');

      // If the response has a content-length of 0 or no JSON content-type, return null
      const contentLength = response.headers.get('content-length');
      if (contentLength === '0' || !hasJsonContent) {
        return null as T;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error(`Network error: ${error}`);
    }
  }

  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  async post<T>(endpoint: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async put<T>(endpoint: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }

  async getBlob(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<{ blob: Blob; fileName?: string }> {
    const url = `${this.baseUrl}${endpoint}`;
    const requestHeaders = await this.getRequestHeaders(headers);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: requestHeaders,
      });

      if (!response.ok) {
        const isAuthError = response.status === 401;
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorResponseBody: unknown = undefined;

        // Try to get error details from response body
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const responseData = await response.json();
            errorResponseBody = responseData;
            if (responseData?.message) {
              errorMessage = responseData.message;
            } else if (responseData?.error) {
              errorMessage = responseData.error;
            }
          } else {
            const text = await response.text();
            errorResponseBody = text;
            if (text) {
              errorMessage = text;
            }
          }
        } catch {
          // Ignore body parsing errors, use default message
        }

        const apiError = new ApiError(
          response.status,
          errorMessage,
          isAuthError,
          errorResponseBody
        );
        throw apiError;
      }

      const blob = await response.blob();

      // Extract filename from Content-Disposition header if available
      let fileName: string | undefined;
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        // Match filename="..." or filename=...
        const match = contentDisposition.match(/filename="?([^"\n;]+)"?/);
        if (match && match[1]) {
          fileName = match[1].trim();
        }
      }

      return { blob, fileName };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error(`Network error: ${error}`);
    }
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    headers?: Record<string, string>
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get auth header without Content-Type (browser will set it with boundary)
    const authHeader = await this.getRequestHeaders();

    // Remove Content-Type to let browser set it with multipart boundary
    const headersWithoutContentType = { ...authHeader };
    delete headersWithoutContentType['Content-Type'];

    const finalHeaders = {
      ...headersWithoutContentType,
      ...headers,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: finalHeaders,
        body: formData,
      });

      if (!response.ok) {
        const isAuthError = response.status === 401;
        let errorMessage = `HTTP error! status: ${response.status}`;
        let responseData = null;

        try {
          responseData = await response.json();
          if (responseData?.message) {
            errorMessage = responseData.message;
          } else if (responseData?.error) {
            errorMessage = responseData.error;
          }
        } catch {
          // Ignore JSON parsing errors
        }

        const apiError = new ApiError(response.status, errorMessage, isAuthError, responseData);
        throw apiError;
      }

      if (response.status === 204) {
        return null as T;
      }

      const contentType = response.headers.get('content-type');
      const hasJsonContent = contentType && contentType.includes('application/json');
      const contentLength = response.headers.get('content-length');

      if (contentLength === '0' || !hasJsonContent) {
        return null as T;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error(`Network error: ${error}`);
    }
  }
}

export const apiClient = new ApiClient();
